import {
  crouch,
  E,
  flick,
  graffitiLine,
  N,
  propsLine,
  ride,
  roll,
  tap,
  targetsLine,
  W,
  type DemoLine,
  type TagWall,
  type TargetRow,
} from "./script";

// theme-park's demo lines (#156), played in this order (`DEMO_LINES` in `demo.ts`).

/** The shooting gallery: where the targets line shoots from, and the row (the map file's `targets`). */
const TARGETS: TargetRow = {
  range: { x: -2, z: -7.5 },
  row: [
    [-10, -16.5],
    [-6, -17.5],
    [-2, -18],
    [2, -17.5],
    [6, -16.5],
  ],
};

// Built on call so the hill site drops them (#229; see `parkV2Lines`).
export const themeParkLines = (): DemoLine[] => [
  {
    id: "theme-midway",
    name: "Kickflip over the Midway",
    mapId: "theme-park",
    // North up the ring-toss pyramid, over the top and down the far side.
    start: { x: 0, z: 8.8, yaw: N, speed: 9 },
    segments: [
      crouch(0.5),
      roll(0.03),
      tap("flip"),
      roll(0.4),
      roll(1.6),
      { dur: 1.2, x: 0, y: -1 },
    ],
    lands: ["Kickflip", "Over the Midway"],
  },
  {
    id: "theme-flume",
    name: "Flume Drop",
    mapId: "theme-park",
    // West up the flume's ramp, over the tower and down the long slide.
    start: { x: -11, z: 0, yaw: W, speed: 9 },
    segments: [
      crouch(0.5),
      roll(0.03),
      tap("grab"),
      roll(0.5),
      roll(2),
      { dur: 1, x: 0, y: -1 },
    ],
    lands: ["Flume Drop"],
  },
  {
    id: "theme-flume-wall",
    name: "Grind the flume's wall: up, across and down",
    mapId: "theme-park",
    // West along the flume's south wall (#170): ollie onto its top up the
    // ramp, lock on again across the deck, then boardslide it down the long side.
    start: { x: -11, z: 2.2, yaw: W, speed: 8 },
    segments: [
      crouch(0.3),
      roll(0.03),
      tap("grind"),
      ride(0.6),
      tap("grind", 0, 1),
      ride(0.4),
      tap("grind", -1, 0),
      ride(1.2),
      roll(1.5),
    ],
    lands: ["50-50", "Boardslide"],
  },
  {
    id: "theme-coaster",
    name: "Boardslide the coaster drop",
    mapId: "theme-park",
    // Off the coaster deck onto the track, down the drop (onto its right-hand rail past the station, #231).
    start: { x: 21.5, z: -19.7, yaw: 1.29, speed: 9 },
    segments: [
      crouch(0.25),
      roll(0.12),
      tap("grind"),
      ride(0.2),
      tap("grind", -1),
      ride(0.45),
      // Ollie off partway down the drop and land past the bottom (#189; off the right-hand rail, #231, the lift hill curves into an ollie much earlier or later), then steer clear of the ramp ahead: hit at that angle it is a crooked vert air (#183).
      tap("ollie"),
      ride(0.7),
      roll(0.6, 0.5),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["Boardslide", "Coaster Drop"],
  },
  {
    id: "theme-coaster-ride",
    name: "Ride the whole coaster",
    mapId: "theme-park",
    // Onto the track off the coaster deck (#189), one 50-50 the whole way: down the drop, up the lift hill out past the fence, round the top, the helix and the station, and off the end back into the park; then turn off the line to miss the rail ahead.
    start: { x: 21.5, z: -19.7, yaw: 1.29, speed: 9 },
    segments: [crouch(0.25), roll(0.12), tap("grind"), ride(16.4), roll(0.75), roll(0.3, -1), roll(2)],
    lands: ["50-50", "Coaster Drop", "End of the Line"],
  },
  {
    id: "theme-smith",
    name: "Smith down the coaster",
    mapId: "theme-park",
    start: { x: 21.5, z: -19.7, yaw: 1.29, speed: 9 },
    segments: [
      crouch(0.25),
      roll(0.12),
      tap("grind"),
      ride(0.2),
      tap("grind", -1, -1),
      ride(0.45),
      // Ollie off partway down the drop and land past the bottom (#189; off the right-hand rail, #231, the lift hill curves into an ollie much earlier or later), then steer clear of the ramp ahead: hit at that angle it is a crooked vert air (#183).
      tap("ollie"),
      ride(0.7),
      roll(0.6, 0.5),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["Smith", "Coaster Drop"],
  },
  {
    id: "theme-lift",
    name: "Up the lift hill",
    mapId: "theme-park",
    start: { x: 9.5, z: -19.9, yaw: 1.508, speed: 9 },
    // Up onto the station deck and ollie off its end, over both of the track's rails (#231).
    segments: [roll(1.6), crouch(0.25), roll(0.75), { dur: 1.5, x: 0, y: -1 }],
    lands: [],
  },
  {
    id: "theme-queue",
    name: "The queue rail, for C-O-M-B-O",
    mapId: "theme-park",
    start: { x: 32.5, z: -6, yaw: W, speed: 9 },
    segments: [crouch(0.25), roll(0.12), tap("grind"), ride(1.5), roll(1)],
    lands: [],
  },
  {
    id: "theme-wave-pool",
    name: "Across the wave pool",
    mapId: "theme-park",
    start: { x: -26, z: -18, yaw: 0, speed: 9 },
    segments: [roll(1.6), { dur: 1.5, x: 0, y: -1 }],
    lands: [],
  },
  {
    id: "theme-spine",
    name: "Over the spine",
    mapId: "theme-park",
    start: { x: 11, z: 0.5, yaw: E, speed: 9 },
    segments: [roll(2), { dur: 1.5, x: 0, y: -1 }],
    lands: [],
  },
  {
    id: "theme-castle",
    name: "Drop in off the castle gate",
    mapId: "theme-park",
    start: { x: 24, z: 22, yaw: N, speed: 9 },
    segments: [crouch(0.25), roll(1.6), { dur: 1.5, x: 0, y: -1 }],
    lands: ["Castle Gate"],
  },
  {
    id: "theme-flips",
    name: "Manual flip combo by the midway",
    mapId: "theme-park",
    start: { x: 0, z: 10, yaw: 0, speed: 9 },
    segments: [
      ...flick([0, 1], [0, -1]),
      { dur: 0.05, x: 0, y: 0 },
      ride(0.158),
      tap("flip", 1, 1),
      ride(0.45),
      tap("flip", -1, 1),
      ride(0.45),
      tap("flip", 1, -1),
      ride(0.45),
      tap("flip", -1),
      ride(0.45),
      { dur: 0.1, x: 0, hold: ["ollie"] },
      roll(1.2),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["Manual"],
  },
  {
    id: "theme-pool",
    name: "Air out of the deep end",
    mapId: "theme-park",
    // West from the deck into the L pool's shallow end (#169), crouched the length of it down the hip, up the deep end's wall and out, grabbing.
    start: { x: -8, z: 13, yaw: W, speed: 9 },
    segments: [crouch(2.8), roll(0.15), tap("grab"), roll(1.2), roll(2)],
    lands: ["Method"],
  },
  {
    id: "theme-handrail",
    name: "Down the pool's handrail",
    mapId: "theme-park",
    // From the spawn straight west onto the handrail down the shallow end's steps (#169), 50-50 into the pool.
    start: { x: -2, z: 15, yaw: W, speed: 9 },
    segments: [crouch(0.4), roll(0.2), tap("grind"), ride(1), roll(1.5)],
    lands: ["50-50"],
  },
  {
    id: "theme-slide",
    name: "Down the water slide",
    mapId: "theme-park",
    // Up the bank onto the slide's tower on the board (#169), crouched down the chute and round its bend, then a pop and a grab off its end into the pool.
    start: { x: 4, z: 7, yaw: W, speed: 9 },
    segments: [crouch(2.5), roll(0.05), tap("grab"), roll(0.4), roll(1.5)],
    lands: ["Method", "Splashdown"],
  },
  {
    id: "theme-slide-wall",
    name: "Grind the water slide's wall",
    mapId: "theme-park",
    // West across the slide's tower, an ollie off its edge onto the chute's south wall (#187), the inside of the bend, one 50-50 down it, round the bend and off the end into the pool.
    start: { x: -14.2, z: 8.3, yaw: W, speed: 6 },
    segments: [crouch(0.1), roll(0.03), tap("grind"), ride(2.5), roll(2)],
    lands: ["50-50", "Splashdown"],
  },
];

/** The "Hit 5 targets" line (#137), played after every map's other lines. */
export const THEME_PARK_TARGETS: DemoLine = targetsLine(
  "theme-park",
  TARGETS,
  "Paint the shooting gallery",
  W,
);

/** The graffiti on the west wall behind the wave pool (the map file's `graffiti`), and where to stand to wash each tag. */
const GRAFFITI: TagWall = {
  face: W,
  stands: [
    [-37.8, -20],
    [-37.8, -15],
    [-37.8, -10],
  ],
};

/** The "Wash off 3 graffiti tags" challenge (#151). */
export const THEME_PARK_GRAFFITI: DemoLine = graffitiLine(
  "theme-park",
  GRAFFITI,
  "Power wash the graffiti off the west wall",
);

/** The washer knocking a cone and a can about (#151): the pair by the midway's south end (the map file's `props`). */
export const THEME_PARK_PROPS: DemoLine = propsLine(
  "theme-park-props",
  "theme-park",
  [11.5, 14],
  0, // facing south (+z)
  [
    [10, 18],
    [13, 18.5],
  ],
  "Power wash a cone and a can across the path",
);
